/**
 * Date and time helpers: the diary's grid maths, and how every date in the
 * portal is shown.
 *
 * The portal carries no date library on purpose, and the diary needs very
 * little: a handful of pure functions over the two shapes the API actually
 * speaks.
 *
 * ## The two shapes, and why both exist
 *
 * The API returns every instant twice. `starts_at` is an ISO-8601 UTC string —
 * the instant, for computing with. `local_starts_at` is `"YYYY-MM-DD HH:MM"` in
 * the *branch's* timezone — for showing.
 *
 * That distinction is the whole reason this file is careful. A branch in Riyadh
 * viewed from a laptop in London must draw its diary on Riyadh's clock, not the
 * laptop's. So the grid works in **branch-local wall-clock minutes** throughout,
 * and only ever converts through `Intl.DateTimeFormat` with an explicit
 * timezone — never through the browser's own offset, which would be a different
 * day for half the world.
 */

/** Minutes since midnight, from a "HH:MM" wall clock. */
export const toMinutes = (hhmm) => {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + (m || 0);
};

/** "HH:MM" from minutes since midnight. */
export const toClock = (minutes) => {
  const m = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

/**
 * A UTC instant expressed in a named timezone, as { date, time, minutes }.
 *
 * `Intl` is what makes this correct across daylight saving without a library:
 * it is given the zone explicitly, so the browser's own offset never enters
 * into it.
 */
export const inZone = (iso, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));

  const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
  // en-CA gives 24-hour time, but midnight can come back as "24".
  const hour = get("hour") === "24" ? "00" : get("hour");

  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${hour}:${get("minute")}`,
    minutes: toMinutes(`${hour}:${get("minute")}`),
  };
};

/** Today's date in a named timezone, as "YYYY-MM-DD". */
export const todayIn = (timeZone) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

/**
 * Shift a "YYYY-MM-DD" by whole days.
 *
 * Anchored at UTC noon rather than midnight: a date built at midnight and
 * shifted can land on the previous day in a negative-offset zone, which is the
 * classic off-by-one that makes a calendar show the wrong week.
 */
export const addDays = (date, days) => {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** 0 = Sunday … 6 = Saturday, matching the API's `day` column. */
export const weekdayOf = (date) => new Date(`${date}T12:00:00Z`).getUTCDay();

/** The Sunday on or before the given date — the week grid's first column. */
export const startOfWeek = (date) => addDays(date, -weekdayOf(date));

/** The seven dates of the week containing `date`. */
export const weekOf = (date) => {
  const first = startOfWeek(date);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
};

/*
 * ## Showing dates and times
 *
 * Every date, time and range the portal shows goes through the four helpers
 * below — one place for the language, the order and the direction.
 *
 * - Each value is formatted as ONE string by `Intl.DateTimeFormat` (never a
 *   date and a time glued together): "25 Oct 2026, 22:29" / "25 أكتوبر 2026،
 *   22:29". Day first in both languages, 24-hour clock, Western digits.
 * - In Arabic the string is wrapped in a right-to-left isolate (RLI … PDI),
 *   the in-text equivalent of `<bdi dir="rtl">`. Without it, a date dropped
 *   into a left-to-right cell, a chart label or a sentence is reordered by the
 *   bidi algorithm and reads "أكتوبر 2026 25 22:29".
 *
 * A value is either a branch-local wall clock as the API sends it
 * ("2026-10-25" or "2026-10-25 22:29", shown as is — it already is the
 * branch's clock) or an ISO instant, shown in `options.timeZone` when given,
 * else in the reader's own zone (see the note on `formatDate`).
 */

/**
 * The Intl locale for dates: Arabic words with Western digits; day-first
 * English ("25 Sep 2026" — en-AE, falling back to en-GB's "25 Sept 2026").
 */
export const dateLocale = (locale) =>
  String(locale ?? "").startsWith("ar") ? "ar-u-nu-latn" : ["en-AE", "en-GB"];

const isArabic = (locale) => String(locale ?? "").startsWith("ar");

const RLI = "\u2067";
const LRI = "\u2066";
const PDI = "\u2069";

/** Keep an Arabic date in its own direction wherever it lands. */
const isolate = (text, locale) => (text && isArabic(locale) ? `${RLI}${text}${PDI}` : text);

const LOCAL_DAY = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_TIME = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?$/;
const CLOCK = /^(\d{2}):(\d{2})(?::\d{2})?$/;

/**
 * A value as `[Date, timeZone]`. A wall clock is pinned to UTC and formatted
 * in UTC, so it shows exactly as sent whatever the reader's own zone.
 */
const toDate = (value, timeZone) => {
  if (value instanceof Date) return [value, timeZone];

  const text = String(value);
  if (LOCAL_DAY.test(text)) return [new Date(`${text}T12:00:00Z`), "UTC"];

  const local = LOCAL_TIME.exec(text);
  if (local) return [new Date(`${local[1]}T${local[2]}:${local[3]}:00Z`), "UTC"];

  const date = new Date(text);

  return Number.isNaN(date.getTime()) ? [null] : [date, timeZone];
};

const format = (value, locale, defaults, options = {}) => {
  if (value === null || value === undefined || value === "") return "";

  const { timeZone, ...rest } = options;
  const [date, zone] = toDate(value, timeZone);
  if (!date) return String(value);

  try {
    return isolate(
      new Intl.DateTimeFormat(dateLocale(locale), {
        ...defaults,
        ...rest,
        timeZone: zone || undefined,
      }).format(date),
      locale
    );
  } catch {
    return String(value);
  }
};

const DAY_PARTS = { day: "numeric", month: "short", year: "numeric" };
const TIME_PARTS = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };

/**
 * A date: "25 Oct 2026" / "25 أكتوبر 2026". Options add or drop parts
 * (`{ weekday: "short", year: undefined }` → "Sun 25 Oct").
 *
 * An instant with no `timeZone` shows in the **reader's** own zone. The diary
 * insists on the branch's clock because a slot at 10:00 in Riyadh is 10:00 to
 * everyone; a client record is the other case: "last visited" spans every
 * branch they have ever been to, so there is no one branch clock to render it
 * on, and the reader's own is the honest answer.
 */
export const formatDate = (value, locale, options) => format(value, locale, DAY_PARTS, options);

/** A date and time: "25 Oct 2026, 22:29" / "25 أكتوبر 2026، 22:29". */
export const formatDateTime = (value, locale, options) =>
  format(value, locale, { ...DAY_PARTS, ...TIME_PARTS }, options);

/**
 * A time of day: "22:29". A bare "22:29" or a wall clock keeps its own time.
 * Digits only, so it needs no isolate.
 */
export const formatTime = (value, locale, options) => {
  if (value === null || value === undefined || value === "") return "";

  const clock = CLOCK.exec(String(value));
  if (clock) return `${clock[1]}:${clock[2]}`;

  const local = LOCAL_TIME.exec(String(value));
  if (local) return `${local[2]}:${local[3]}`;

  return format(value, "en", TIME_PARTS, options);
};

/**
 * A span of days: "1 – 7 Sep 2026", "28 Sep – 4 Oct 2026"; one day alone is
 * just that date. Options as for `formatDate` (e.g. `{ year: undefined }`).
 */
export const formatDateRange = (from, to, locale, options = {}) => {
  if (!from || !to) return "";
  if (from === to) return formatDate(to, locale, options);

  const [start] = toDate(from);
  const [end] = toDate(to);
  if (!start || !end) return `${formatDate(from, locale, options)} – ${formatDate(to, locale, options)}`;

  const parts = { ...DAY_PARTS, ...options, timeZone: "UTC" };

  // Arabic, same month: "1 – 7 سبتمبر 2026". Left alone, the bidi algorithm
  // turns the dash between two numbers right-to-left and shows "7 – 1";
  // isolating the numeric run keeps it in reading order.
  if (isArabic(locale) && String(from).slice(0, 7) === String(to).slice(0, 7)) {
    const day = (date) =>
      new Intl.DateTimeFormat(dateLocale(locale), { day: parts.day, timeZone: "UTC" }).format(date);
    const monthYear = new Intl.DateTimeFormat(dateLocale(locale), {
      ...parts,
      day: undefined,
      weekday: undefined,
    }).format(end);

    return isolate(`${LRI}${day(start)} – ${day(end)}${PDI} ${monthYear}`, locale);
  }

  try {
    return isolate(new Intl.DateTimeFormat(dateLocale(locale), parts).formatRange(start, end), locale);
  } catch {
    return `${formatDate(from, locale, options)} – ${formatDate(to, locale, options)}`;
  }
};

/** A month-and-year heading for the diary's toolbar. */
export const formatMonth = (date, locale) => formatDate(date, locale, { day: undefined, month: "long" });

/**
 * The branch-local wall-clock span an appointment occupies on a given date, in
 * minutes since midnight — what the grid positions a block with.
 *
 * A booking can straddle midnight in principle, so it is clamped to the day
 * being drawn rather than allowed to overflow the column.
 */
export const spanOn = (startIso, endIso, date, timeZone) => {
  const start = inZone(startIso, timeZone);
  const end = inZone(endIso, timeZone);

  if (start.date > date || end.date < date) return null;

  return {
    from: start.date === date ? start.minutes : 0,
    to: end.date === date ? end.minutes : 24 * 60,
  };
};

/** Whether two wall-clock spans overlap. Touching at the edges does not count. */
export const overlaps = (a, b) => a.from < b.to && a.to > b.from;

/**
 * Lay overlapping blocks out side by side.
 *
 * Two bookings on the same provider should not happen — the engine prevents it
 * — but a branch-wide block and a booking can share a column, and a diary that
 * silently stacks them on top of each other hides one of them. Each block gets
 * a `columns` count and its own `column`, which the view turns into a width.
 */
export const layout = (blocks) => {
  const sorted = [...blocks].sort((a, b) => a.span.from - b.span.from);
  const placed = [];

  sorted.forEach((block) => {
    // The lowest column index not already taken by something overlapping.
    const taken = placed
      .filter((other) => overlaps(other.span, block.span))
      .map((other) => other.column);

    let column = 0;
    while (taken.includes(column)) column += 1;

    placed.push({ ...block, column });
  });

  // Every member of an overlapping cluster needs the same width, so the count
  // is resolved after placement rather than during it.
  return placed.map((block) => {
    const cluster = placed.filter((other) => overlaps(other.span, block.span));
    return { ...block, columns: Math.max(...cluster.map((c) => c.column)) + 1 };
  });
};
